import { ThemeProvider, StyledEngineProvider } from '@mui/material/styles';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import DashboardBanners from 'react/member/card/components/dashboard/DashboardBanners';
import DashboardHeader from 'react/member/card/components/dashboard/DashboardHeader';
import DashboardTabs from 'react/member/card/components/dashboard_tabs/DashboardTabs';
import DashboardCardProvider from 'react/member/card/components/onboarding_widget/DashboardCardContext';
import OnboardingWidget from 'react/member/card/components/onboarding_widget/OnboardingWidget';
import CustomerBannerNotifications from 'react/member/components/dashboard/customer_banners/customer_banner_notifications';
import { estimatedArrivalShape } from 'react/member/shapes/estimated_arrival_shape';
import InProductAdShape from 'react/member/shapes/in_product_ad_shape';
import theme from 'react/shared/theme/Theme';

export default function DashboardIndex({
  ableProgram,
  accountFeeSettings,
  accountId,
  address,
  allAccounts,
  bankAccountNumber,
  banners,
  canEdit,
  card,
  cardActivated,
  cardBalance,
  cardholder,
  cardholderName,
  cardLast4,
  cardOpen,
  cardPresent,
  cardProgramId,
  cardProgramPresent,
  cardReference,
  cardStatus,
  compromisedCard,
  contactEmail,
  copyableBankAccounts,
  defaultAccount,
  dischargeFamilyFunderAllowed,
  displayAccountInfoTab,
  displayAlertsTab,
  displayDisputedCardBanner,
  displayFundingTab,
  displayOnboardingWidget,
  displayReportsTab,
  displaySpendingMonitorTab,
  displayTransactionsTab,
  estimatedArrival,
  familyFunders,
  familyFundingAllowed,
  filteredBankAccounts,
  firstCardOrder,
  fraudBlockedCard,
  fullName,
  inProductAdReportsTab,
  inProductAdTransactionsTab,
  isFamilyFunder,
  isNextStepProgram,
  isViewOnly,
  last4Digits,
  mockPublicToken,
  monthlyStatementDates,
  msaLink,
  nonDeletedCardExists,
  notAbleActProgram,
  programName,
  pseudoDda,
  routingNumber,
  ruleSetId,
  stableProgram,
  status,
  subOrgId,
  supportADTollfree,
  supportCHTollfree,
  supportEmail,
  userSlug,
}) {
  const queryClient = useMemo(() => new QueryClient(), []);

  const props = {
    accountId,
    address,
    cardholder,
    msaLink,
    programName,
    supportADTollfree,
    supportCHTollfree,
    supportEmail,
  };

  return (
    <QueryClientProvider client={queryClient}>
      <StyledEngineProvider injectFirst>
        <ThemeProvider theme={theme}>
          <DashboardCardProvider props={props}>
            <DashboardHeader
              accountId={accountId}
              allAccounts={allAccounts}
              defaultAccount={defaultAccount}
              fullName={fullName}
              last4Digits={last4Digits}
              status={status}
            />
            {displayOnboardingWidget && (
              <OnboardingWidget
                accountId={accountId}
                address={address}
                cardBalance={cardBalance}
                cardProgramId={cardProgramId}
                cardStatus={cardStatus}
                cardholder={cardholder}
                contactEmail={contactEmail}
                displayFundingTab={displayFundingTab}
                estimatedArrival={estimatedArrival}
                firstCardOrder={firstCardOrder}
                msaLink={msaLink}
                programName={programName}
                status={status}
                supportADTollfree={supportADTollfree}
                supportCHTollfree={supportCHTollfree}
                supportEmail={supportEmail}
                userSlug={userSlug}
              />
            )}
            <CustomerBannerNotifications banners={banners} />
            <DashboardBanners
              accountId={accountId}
              compromisedCard={compromisedCard}
              displayDisputedCardBanner={displayDisputedCardBanner}
              fraudBlockedCard={fraudBlockedCard}
              isFamilyFunder={isFamilyFunder}
              isViewOnly={isViewOnly}
              status={status}
            />
            <DashboardTabs
              ableProgram={ableProgram}
              accountFeeSettings={accountFeeSettings}
              accountId={accountId}
              bankAccountNumber={bankAccountNumber}
              canEdit={canEdit}
              card={card}
              cardActivated={cardActivated}
              cardBalance={`${cardBalance}`}
              cardLast4={cardLast4}
              cardOpen={cardOpen}
              cardPresent={cardPresent}
              cardProgramPresent={cardProgramPresent}
              cardReference={cardReference}
              cardholderName={cardholderName}
              copyableBankAccounts={copyableBankAccounts}
              dischargeFamilyFunderAllowed={dischargeFamilyFunderAllowed}
              displayAccountInfoTab={displayAccountInfoTab}
              displayAlertsTab={displayAlertsTab}
              displayFundingTab={displayFundingTab}
              displayReportsTab={displayReportsTab}
              displaySpendingMonitorTab={displaySpendingMonitorTab}
              displayTransactionsTab={displayTransactionsTab}
              familyFunders={familyFunders}
              familyFundingAllowed={familyFundingAllowed}
              filteredBankAccounts={filteredBankAccounts}
              inProductAdReportsTab={inProductAdReportsTab}
              inProductAdTransactionsTab={inProductAdTransactionsTab}
              isFamilyFunder={isFamilyFunder}
              isNextStepProgram={isNextStepProgram}
              isViewOnly={isViewOnly}
              mockPublicToken={mockPublicToken}
              monthlyStatementDates={monthlyStatementDates}
              msaLink={msaLink}
              nonDeletedCardExists={nonDeletedCardExists}
              notAbleActProgram={notAbleActProgram}
              pseudoDda={pseudoDda}
              routingNumber={routingNumber}
              ruleSetId={ruleSetId}
              stableProgram={stableProgram}
              subOrgId={subOrgId}
            />
          </DashboardCardProvider>
        </ThemeProvider>
      </StyledEngineProvider>
    </QueryClientProvider>
  );
}

DashboardIndex.propTypes = {
  ableProgram: PropTypes.bool,
  accountFeeSettings: PropTypes.object,
  accountId: PropTypes.string.isRequired,
  address: PropTypes.object,
  allAccounts: PropTypes.array,
  bankAccountNumber: PropTypes.string,
  banners: PropTypes.array,
  canEdit: PropTypes.bool,
  card: PropTypes.object,
  cardholder: PropTypes.shape({
    first_name: PropTypes.string,
    last_name: PropTypes.string,
    mobile: PropTypes.string,
    nickname: PropTypes.string,
    dob: PropTypes.string,
    card_name: PropTypes.string,
  }),
  cardActivated: PropTypes.bool,
  cardBalance: PropTypes.number.isRequired,
  cardholderName: PropTypes.string,
  cardLast4: PropTypes.string,
  cardOpen: PropTypes.bool,
  cardPresent: PropTypes.bool,
  cardProgramId: PropTypes.number,
  cardProgramPresent: PropTypes.bool,
  cardReference: PropTypes.string,
  cardStatus: PropTypes.string,
  compromisedCard: PropTypes.object,
  contactEmail: PropTypes.string.isRequired,
  copyableBankAccounts: PropTypes.array,
  defaultAccount: PropTypes.bool,
  dischargeFamilyFunderAllowed: PropTypes.bool,
  displayAccountInfoTab: PropTypes.bool,
  displayAlertsTab: PropTypes.bool,
  displayDisputedCardBanner: PropTypes.bool,
  displayFundingTab: PropTypes.bool,
  displayOnboardingWidget: PropTypes.bool,
  displayReportsTab: PropTypes.bool,
  displaySpendingMonitorTab: PropTypes.bool,
  displayTransactionsTab: PropTypes.bool,
  estimatedArrival: estimatedArrivalShape.isRequired,
  familyFunders: PropTypes.array,
  familyFundingAllowed: PropTypes.bool,
  filteredBankAccounts: PropTypes.any,
  firstCardOrder: PropTypes.bool.isRequired,
  fraudBlockedCard: PropTypes.object,
  fullName: PropTypes.string,
  inProductAdReportsTab: InProductAdShape,
  inProductAdTransactionsTab: InProductAdShape,
  isFamilyFunder: PropTypes.bool,
  isNextStepProgram: PropTypes.bool,
  isViewOnly: PropTypes.bool,
  last4Digits: PropTypes.string,
  mockPublicToken: PropTypes.string,
  monthlyStatementDates: PropTypes.arrayOf(
    PropTypes.shape({
      date: PropTypes.string.isRequired,
      card_slug: PropTypes.string.isRequired,
      last_4_digits: PropTypes.string.isRequired,
    }).isRequired,
  ),
  msaLink: PropTypes.string,
  nonDeletedCardExists: PropTypes.bool,
  notAbleActProgram: PropTypes.bool,
  programName: PropTypes.string.isRequired,
  pseudoDda: PropTypes.string,
  routingNumber: PropTypes.string,
  ruleSetId: PropTypes.number,
  stableProgram: PropTypes.bool,
  status: PropTypes.string.isRequired,
  subOrgId: PropTypes.number,
  supportADTollfree: PropTypes.string.isRequired,
  supportCHTollfree: PropTypes.string.isRequired,
  supportEmail: PropTypes.string,
  userSlug: PropTypes.string.isRequired,
};
